import {BACKEND_BASE_URL, request} from './apiClient';

// ─── Opalite University (video education) backend client ─────────────────────
//
// The catalog, audience filtering, per-user watch progress, AND playback
// access are all owned by the backend (calliope-backend
// src/routes/university.ts); this module is the thin HTTP layer. Video files
// live in a private GCS bucket — clients never receive storage URLs in the
// catalog. Playback is a two-step: authenticated callers exchange their
// Firebase token for a short-lived signed URL; the pre-auth /start page uses
// the unauthenticated public-stream redirect (public videos only).

export interface UniversityCategory {
  id: string;
  label: string;
}

export interface UniversityVideo {
  id: string;
  title: string;
  description: string;
  category: string;
  /** A recording exists. false = placeholder ("coming soon" frame). */
  hasVideo: boolean;
  posterUrl: string | null;
  durationSec: number;
  /** App routes where this video is offered as a contextual tip. */
  contextRoutes: string[];
}

export interface UniversityVideoProgress {
  watchedAt: string | null;
  dismissedAt: string | null;
}

export interface UniversityGuidedFlags {
  /** The user signed in via the assisted /start flow at some point. */
  active: boolean;
  showGuidedWelcome: boolean;
  showGettingStartedCard: boolean;
  playlistWatchedCount: number;
  playlistTotal: number;
}

export interface UniversityCatalogPayload {
  categories: UniversityCategory[];
  videos: UniversityVideo[];
  /** Ordered Getting Started playlist (video ids), audience-filtered. */
  playlist: string[];
  progress: Record<string, UniversityVideoProgress>;
  guided: UniversityGuidedFlags;
}

export type UniversityProgressEvent = 'watched' | 'dismissed';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/** Keep optional education data from ever reaching the clinical app shell. */
export function isUniversityCatalogPayload(
  value: unknown,
): value is UniversityCatalogPayload {
  if (!isRecord(value) || !isRecord(value.guided)) return false;
  const {guided} = value;
  return (
    Array.isArray(value.categories) &&
    Array.isArray(value.videos) &&
    value.videos.every(
      video =>
        isRecord(video) &&
        typeof video.id === 'string' &&
        typeof video.title === 'string' &&
        typeof video.durationSec === 'number' &&
        Array.isArray(video.contextRoutes),
    ) &&
    Array.isArray(value.playlist) &&
    value.playlist.every(id => typeof id === 'string') &&
    isRecord(value.progress) &&
    typeof guided.active === 'boolean' &&
    typeof guided.showGuidedWelcome === 'boolean' &&
    typeof guided.showGettingStartedCard === 'boolean' &&
    typeof guided.playlistWatchedCount === 'number' &&
    typeof guided.playlistTotal === 'number'
  );
}

interface GuidedStartHandoff {
  version: 2;
  id: string;
  createdAt: number;
}

const GUIDED_START_VERSION = 2;
const GUIDED_START_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const GUIDED_START_ID_RE = /^[A-Za-z0-9_-]{16,64}$/;
const LEGACY_GUIDED_START_STORAGE_KEY = 'university_guided_start_pending';

// Minimal, versioned handoff state. The unique id makes backend retries
// idempotent: reloading before the welcome renders cannot reset progress a
// second time or overwrite another education update.
export const GUIDED_START_STORAGE_KEY = 'university_guided_start:v2';
export const GUIDED_START_QUERY_PARAM = 'guidedStart';

function createGuidedStartId(): string {
  try {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to the compatibility id */
  }
  return `${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2)}_${Math.random().toString(36).slice(2)}`.slice(0, 64);
}

function isValidHandoff(value: unknown): value is GuidedStartHandoff {
  if (!isRecord(value)) return false;
  return (
    value.version === GUIDED_START_VERSION &&
    typeof value.id === 'string' &&
    GUIDED_START_ID_RE.test(value.id) &&
    typeof value.createdAt === 'number' &&
    Number.isFinite(value.createdAt) &&
    Date.now() - value.createdAt >= 0 &&
    Date.now() - value.createdAt <= GUIDED_START_MAX_AGE_MS
  );
}

function readStoredGuidedStart(): GuidedStartHandoff | null {
  try {
    const raw = localStorage.getItem(GUIDED_START_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (isValidHandoff(parsed)) return parsed;
    localStorage.removeItem(GUIDED_START_STORAGE_KEY);
  } catch {
    try {
      localStorage.removeItem(GUIDED_START_STORAGE_KEY);
    } catch {
      /* blocked storage is treated as absent */
    }
  }
  return null;
}

function queryGuidedStartId(): string | null {
  try {
    const id = new URLSearchParams(window.location.search).get(
      GUIDED_START_QUERY_PARAM,
    );
    return id && GUIDED_START_ID_RE.test(id) ? id : null;
  } catch {
    return null;
  }
}

export function setGuidedStartPending(): string {
  const handoff: GuidedStartHandoff = {
    version: GUIDED_START_VERSION,
    id: createGuidedStartId(),
    createdAt: Date.now(),
  };
  try {
    localStorage.setItem(GUIDED_START_STORAGE_KEY, JSON.stringify(handoff));
    localStorage.removeItem(LEGACY_GUIDED_START_STORAGE_KEY);
  } catch {
    // The URL marker remains authoritative when storage is blocked.
  }
  return handoff.id;
}

export function getGuidedStartPendingId(): string | null {
  return queryGuidedStartId() || readStoredGuidedStart()?.id || null;
}

export function clearGuidedStartPending(id?: string): void {
  try {
    localStorage.removeItem(GUIDED_START_STORAGE_KEY);
    localStorage.removeItem(LEGACY_GUIDED_START_STORAGE_KEY);
  } catch {
    /* noop */
  }
  try {
    const url = new URL(window.location.href);
    const queryId = url.searchParams.get(GUIDED_START_QUERY_PARAM);
    if (!queryId || (id && queryId !== id)) return;
    url.searchParams.delete(GUIDED_START_QUERY_PARAM);
    const search = url.searchParams.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${search ? `?${search}` : ''}${url.hash}`,
    );
  } catch {
    /* URL cleanup is best-effort */
  }
}

/** Use a clean document load so an authenticated /start visit cannot reuse a stale catalog. */
export function continueToGuidedApp(id: string): void {
  window.location.assign(
    `/?${GUIDED_START_QUERY_PARAM}=${encodeURIComponent(id)}`,
  );
}

/** Full catalog + caller progress + guided flags (authenticated). */
export async function fetchUniversityCatalog(): Promise<UniversityCatalogPayload> {
  const payload: unknown = await request('/api/v2/university/catalog');
  if (!isUniversityCatalogPayload(payload)) {
    throw new Error('Invalid University catalog response');
  }
  return payload;
}

/** Public (pre-auth) subset for the /start page. */
export function fetchPublicUniversityCatalog(): Promise<{
  videos: UniversityVideo[];
}> {
  return request('/api/v2/university/public-catalog', {auth: false});
}

/**
 * Unauthenticated playback source for PUBLIC videos: the backend 302s the
 * browser's media fetch to a short-lived signed URL. Only the /start page
 * uses this; everything else goes through fetchVideoPlaybackUrl.
 */
export function publicVideoStreamUrl(videoId: string): string {
  return `${BACKEND_BASE_URL}/api/v2/university/videos/${encodeURIComponent(
    videoId,
  )}/public-stream`;
}

/**
 * Exchange the caller's Firebase token for a short-lived signed playback URL
 * (the only way protected videos leave the private bucket). Fetched at play
 * time so a long-lived tab never holds an expired URL.
 */
export async function fetchVideoPlaybackUrl(videoId: string): Promise<string> {
  const data = await request(
    `/api/v2/university/videos/${encodeURIComponent(videoId)}/playback-url`,
  );
  return data.url as string;
}

export function reportVideoProgress(
  videoId: string,
  event: UniversityProgressEvent,
): Promise<void> {
  return request('/api/v2/university/progress', {
    method: 'POST',
    body: JSON.stringify({videoId, event}),
  });
}

export function reportGuidedStart(guidedStartId: string): Promise<void> {
  return request('/api/v2/university/progress', {
    method: 'POST',
    body: JSON.stringify({guidedStart: true, guidedStartId}),
  });
}

export function reportGuidedWelcomeSeen(): Promise<void> {
  return request('/api/v2/university/progress', {
    method: 'POST',
    body: JSON.stringify({welcomeSeen: true}),
  });
}

export function reportGettingStartedDismissed(): Promise<void> {
  return request('/api/v2/university/progress', {
    method: 'POST',
    body: JSON.stringify({gettingStartedDismissed: true}),
  });
}
